import { ref, computed, onMounted, onBeforeUnmount, watch, type MaybeRefOrGetter, toValue } from 'vue';
import { ibWS } from '@ibgroup-tws-webapps/ib.utils';
import { useUserState } from '@/stores/useUserState';

export interface FlexbestBidAskStreamOptions {
  seedBid?: MaybeRefOrGetter<number | undefined>;
  seedAsk?: MaybeRefOrGetter<number | undefined>;
  isLive?: MaybeRefOrGetter<boolean>;
}

export function useFlexbestBidAskStream(
  conidExPairs: MaybeRefOrGetter<string[]>,
  options: FlexbestBidAskStreamOptions = {},
) {
  const bid = ref<number | null>(null);
  const ask = ref<number | null>(null);
  const userState = useUserState();

  const shouldStream = computed(() => !userState.usePublicApi);

  let requestId: string | null = null;
  let subscription: { unsubscribe(): void } | null = null;
  let generation = 0;
  const mounted = ref(false);

  async function subscribe() {
    const currentGeneration = ++generation;
    const pairs = toValue(conidExPairs);
    if (!pairs || pairs.length === 0) return;

    const webAppId = await ibWS.getWebAppId();
    if (currentGeneration !== generation) return;
    requestId = `ec-flexbest-${pairs.join(',')}-${webAppId}`;

    ibWS.sendMessage({
      message: {
        command: 'SEND',
        headers: {
          destination: '/event-contract/flexbest-bid-ask',
          'x-target': 'mdfarm',
          id: requestId,
        },
        body: { conid_exs: pairs },
      },
    });

    const observable = ibWS.receive(requestId);
    if (observable) {
      subscription = observable.subscribe({
        /* eslint-disable @typescript-eslint/no-explicit-any */
        next: (msg: any) => {
          if (currentGeneration !== generation) return;
          const body = msg?.payload ?? msg?.body ?? msg;
          if (body && typeof body.bid !== 'undefined') {
            const bidVal = Number(body.bid);
            if (!isNaN(bidVal)) bid.value = bidVal;
          }
          if (body && typeof body.ask !== 'undefined') {
            const askVal = Number(body.ask);
            if (!isNaN(askVal)) ask.value = askVal;
          }
        },
      });
    }
  }

  function unsubscribe() {
    generation++;
    bid.value = null;
    ask.value = null;
    if (requestId) {
      ibWS.sendMessage({
        message: {
          command: 'SEND',
          headers: {
            destination: '/md/stream/unsubscribe',
            'x-target': 'mdfarm',
            id: requestId,
          },
        },
      });
    }
    subscription?.unsubscribe();
    subscription = null;
    requestId = null;
  }

  onMounted(() => {
    mounted.value = true;
  });

  onBeforeUnmount(unsubscribe);

  watch(
    [
      mounted,
      shouldStream,
      () => JSON.stringify(toValue(conidExPairs)),
      () => options.isLive === undefined || toValue(options.isLive),
    ],
    () => {
      unsubscribe();
      if (
        mounted.value &&
        shouldStream.value &&
        toValue(conidExPairs).length > 0 &&
        (options.isLive === undefined || toValue(options.isLive))
      ) {
        void subscribe();
      }
    },
  );

  return {
    bid: computed(() => bid.value ?? toValue(options.seedBid) ?? null),
    ask: computed(() => ask.value ?? toValue(options.seedAsk) ?? null),
  };
}
